import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@descope/react-sdk';

import { IndustryDTO, useGetIndustries } from '@/api/generated';

const IndustryContext = createContext<{
  industries: IndustryDTO[] | undefined;
  refreshIndustries: () => void;
  industryMap: Map<string, IndustryDTO>;
  industriesOptions: { id: string; label: string }[];
}>({
  industries: undefined,
  refreshIndustries: () => {},
  industryMap: new Map(),
  industriesOptions: []
});

interface IndustryProviderProps {
  children: ReactNode;
}

export const IndustryProvider: FC<IndustryProviderProps> = ({ children }) => {
  const { isAuthenticated } = useSession();
  const { t, i18n } = useTranslation('filters');
  const { data: industries, refetch: refreshIndustries } = useGetIndustries({ query: { enabled: isAuthenticated } });

  const contextValue = useMemo(
    () => ({
      industries,
      refreshIndustries,
      industryMap: new Map(industries?.map(industry => [industry.id!, industry])),
      industriesOptions: (industries ?? [])
        .filter(({ id }) => id != null)
        .map(({ id, key }) => ({ id: id!, label: t(`filters.options.industry.${key}`) }))
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when locale changes
    [industries, refreshIndustries, t, i18n.language]
  );

  return <IndustryContext.Provider value={contextValue}>{children}</IndustryContext.Provider>;
};

export const useIndustries = () => useContext(IndustryContext);
